import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { filtroDemo } from '../../data/repositories/FiltroDemo';
import { useDemoStore } from '../stores/demoStore';
import { useSesionStore } from '../stores/sesionStore';

interface Resultado {
  /** `false` cuando el conmutador no tiene sentido para este usuario. */
  disponible: boolean;
  ocultando: boolean;
  alternar: () => void;
}

/**
 * Conmutador para apartar de la vista el cliente de demostración.
 *
 * Sólo se le ofrece al superadministrador: es el único que ve varios clientes
 * a la vez y, por tanto, el único a quien la cuenta de muestra le estorba.
 *
 * Al cambiarlo se vacían las consultas guardadas, porque el filtro va en la
 * consulta y no en los datos ya traídos: sin eso, las pantallas seguirían
 * enseñando lo de antes hasta que caducasen solas.
 */
export function useOcultarDemo(): Resultado {
  const clienteConsultas = useQueryClient();
  const perfil = useSesionStore((s) => s.perfil);
  const ocultar = useDemoStore((s) => s.ocultar);
  const establecer = useDemoStore((s) => s.establecer);

  const esSuperadmin = perfil?.rol.esSuperadmin ?? false;
  const [identificado, setIdentificado] = useState(false);

  // Se averigua cuál es el cliente de muestra una vez por sesión. Si no hay
  // ninguno, el conmutador no se ofrece: no habría nada que ocultar.
  //
  // El hook vive ahora en la cabecera de cada pantalla, así que se monta en
  // cada navegación: sólo se invalidan las consultas cuando aplicar la
  // preferencia cambia algo de verdad, no en cada montaje.
  useEffect(() => {
    if (!esSuperadmin) return;

    let vigente = true;
    void filtroDemo.preparar().then(() => {
      if (!vigente) return;

      setIdentificado(filtroDemo.identificado);

      const antes = filtroDemo.aplica;
      filtroDemo.establecerActivo(ocultar && filtroDemo.identificado);
      if (filtroDemo.aplica !== antes) void clienteConsultas.invalidateQueries();
    });

    return () => {
      vigente = false;
    };
    // Sólo al montar: la preferencia guardada se aplica en cuanto se sabe
    // cuál es el cliente, y los cambios posteriores pasan por `alternar`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esSuperadmin]);

  const alternar = useCallback(() => {
    const siguiente = !ocultar;

    establecer(siguiente);
    filtroDemo.establecerActivo(siguiente);
    void clienteConsultas.invalidateQueries();
  }, [ocultar, establecer, clienteConsultas]);

  return {
    disponible: esSuperadmin && identificado,
    ocultando: ocultar,
    alternar,
  };
}
